<template>
  <div class="material-list">
    <div class="page-header">
      <h2>原料列表</h2>
      <a-button type="primary" @click="openEdit(null)">
        <template #icon><PlusOutlined /></template>
        新增原料
      </a-button>
    </div>

    <a-card class="filter-card">
      <a-form layout="inline">
        <a-form-item label="类型">
          <a-select v-model:value="searchForm.type" placeholder="全部" allow-clear style="width: 140px">
            <a-select-option value="FABRIC">面料</a-select-option>
            <a-select-option value="ACCESSORY">辅料</a-select-option>
            <a-select-option value="CONSUMABLE">耗材</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="供应商">
          <a-auto-complete
            v-model:value="searchForm.supplier"
            :options="suppliers"
            :filter-option="supplierFilter"
            placeholder="供应商"
            allow-clear
            style="width: 200px"
          />
        </a-form-item>
        <a-form-item label="名称">
          <a-input v-model:value="searchForm.name" placeholder="请输入名称" allow-clear style="width: 200px" />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" @click="loadList">
              <template #icon><SearchOutlined /></template>
              查询
            </a-button>
            <a-button @click="handleReset">重置</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card>
      <a-table
        :columns="columns"
        :data-source="list"
        :loading="loading"
        :pagination="false"
        row-key="id"
        size="middle"
        :scroll="{ x: 1600 }"
        :sticky="{ offsetScroll: 0, getContainer: getScrollContainer }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'image'">
            <a-image
              v-if="record.imageUrl"
              :src="$thumb(record.imageUrl)"
              :width="48"
              :height="48"
              style="object-fit: cover; border-radius: 4px"
              :preview="{ src: record.imageUrl }"
            />
            <div v-else class="no-image">无</div>
          </template>
          <template v-if="column.key === 'type'">
            <a-tag :color="typeColor(record.type)">
              {{ typeLabel(record.type) }}
            </a-tag>
          </template>
          <template v-if="column.key === 'unit'">{{ unitText(record.unit) }}</template>
          <template v-if="column.key === 'specification'">
            <a-tooltip v-if="record.specification" :title="record.specification" placement="topLeft" color="#fff" overlay-class-name="spec-tooltip">
              <span>{{ record.specification }}</span>
            </a-tooltip>
          </template>
          <template v-if="column.key === 'stock'">
            <span :class="stockClass(record)">
              {{ formatNum(record.stock) }} {{ unitText(record.unit) }}
            </span>
          </template>
          <template v-if="column.key === 'totalPurchased'">{{ formatNum(record.totalPurchased) }}</template>
          <template v-if="column.key === 'totalUsed'">{{ formatNum(record.totalUsed) }}</template>
          <template v-if="column.key === 'latestUnitPrice'">
            <span v-if="record.latestUnitPrice != null">{{ Number(record.latestUnitPrice).toFixed(2) }}</span>
            <span v-else style="color: #ccc">-</span>
          </template>
          <template v-if="column.key === 'suppliers'">
            <a-tooltip v-if="record.suppliers" :title="record.suppliers" color="#fff" overlay-class-name="spec-tooltip" placement="topLeft">
              <span class="supplier-tags">
                <a-tag v-for="s in (record.suppliers || '').split(',').filter(Boolean)" :key="s" style="margin-right: 4px">
                  {{ s }}
                </a-tag>
              </span>
            </a-tooltip>
            <span v-else style="color:#ccc">-</span>
          </template>
          <template v-if="column.key === 'action'">
            <a-space :size="4">
              <a-button type="link" size="small" @click="openEdit(record)">编辑</a-button>
              <a-button type="link" size="small" style="color:#fa8c16" @click="openStockAdjust(record)">改库存</a-button>
              <a-popconfirm title="确定删除该原料吗？" @confirm="handleDelete(record)">
                <a-button type="link" size="small" danger>删除</a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="adjustVisible"
      title="调整库存"
      @ok="handleAdjustSubmit"
      :confirm-loading="adjustSubmitting"
      width="440px"
    >
      <a-form layout="vertical" v-if="adjustForm.materialId">
        <a-descriptions :column="1" size="small" bordered style="margin-bottom: 16px">
          <a-descriptions-item label="原料">{{ adjustForm.name }}</a-descriptions-item>
          <a-descriptions-item label="当前库存">{{ adjustForm.currentDisplay }} {{ adjustForm.unitDisplay }}</a-descriptions-item>
        </a-descriptions>
        <a-form-item label="调整后库存" required>
          <a-input-number
            v-model:value="adjustForm.target"
            :min="0"
            :precision="2"
            style="width: 200px"
            placeholder="0"
          />
          <span style="margin-left: 12px; color: #999">{{ adjustForm.unitDisplay }}</span>
          <div v-if="adjustDelta !== 0" :class="['adjust-delta', adjustDelta > 0 ? 'plus' : 'minus']">
            将{{ adjustDelta > 0 ? '增加' : '减少' }} {{ Math.abs(adjustDelta).toFixed(2).replace(/\.00$/, '') }} {{ adjustForm.unitDisplay }}
          </div>
        </a-form-item>
        <a-form-item label="说明">
          <a-input v-model:value="adjustForm.remark" placeholder="调整原因（可选）" :maxlength="200" />
        </a-form-item>
      </a-form>
    </a-modal>

    <a-modal
      v-model:open="editVisible"
      :title="form.id ? '编辑原料' : '新增原料'"
      @ok="handleSubmit"
      :confirm-loading="submitting"
      width="620px"
    >
      <a-form layout="vertical">
        <a-form-item label="名称" required>
          <a-input v-model:value="form.name" placeholder="如：白色纯棉布150cm" :maxlength="200" />
        </a-form-item>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="类型" required>
              <a-radio-group v-model:value="form.type">
                <a-radio value="FABRIC">面料</a-radio>
                <a-radio value="ACCESSORY">辅料</a-radio>
                <a-radio value="CONSUMABLE">耗材</a-radio>
              </a-radio-group>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="单位" required>
              <a-auto-complete
                v-model:value="form.unit"
                :options="unitOptions"
                placeholder="如：米、码、个、卷、件…"
                :filter-option="unitFilter"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>
        <a-form-item label="规格">
          <a-input v-model:value="form.specification" placeholder="如：150cm幅宽（可选）" :maxlength="200" />
        </a-form-item>
        <a-form-item label="供应商">
          <a-select
            v-model:value="form.supplierList"
            mode="tags"
            :options="supplierOptions"
            placeholder="输入或选择供应商，回车确认（可多个）"
            style="width: 100%"
            :token-separators="[',', '，']"
          />
        </a-form-item>
        <a-form-item label="图片">
          <a-upload
            list-type="picture-card"
            :file-list="imageFileList"
            :before-upload="beforeUpload"
            @remove="handleImageRemove"
            accept="image/*"
            :max-count="1"
          >
            <div v-if="imageFileList.length < 1">
              <PlusOutlined />
              <div style="margin-top: 8px">上传</div>
            </div>
          </a-upload>
        </a-form-item>
        <a-form-item label="备注">
          <a-textarea v-model:value="form.remark" :rows="2" :maxlength="500" show-count />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, shallowRef, reactive, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { PlusOutlined, SearchOutlined } from '@ant-design/icons-vue'
import { materialApi, uploadApi } from '@/api'

export default {
  name: 'MaterialList',
  components: { PlusOutlined, SearchOutlined },
  setup() {
    const loading = ref(false)
    const submitting = ref(false)
    const list = shallowRef([])  // 纯展示大列表：shallowRef 免深度响应式
    const searchForm = reactive({ type: undefined, name: '', supplier: '' })
    const editVisible = ref(false)
    const form = reactive({
      id: null, name: '', type: 'FABRIC', unit: '米',
      specification: '', imageUrl: '', supplierList: [], remark: ''
    })
    const suppliers = ref([])
    const supplierFilter = (input, option) => (option.value || '').includes(input)
    const supplierOptions = computed(() => suppliers.value)

    // 库存调整
    const adjustVisible = ref(false)
    const adjustSubmitting = ref(false)
    const adjustForm = reactive({
      materialId: null,
      name: '',
      current: 0,
      currentDisplay: '0',
      unitDisplay: '',
      target: 0,
      remark: ''
    })
    const adjustDelta = computed(() => Number(adjustForm.target || 0) - Number(adjustForm.current || 0))

    const openStockAdjust = (record) => {
      adjustForm.materialId = record.id
      adjustForm.name = record.name
      adjustForm.current = Number(record.stock || 0)
      adjustForm.currentDisplay = Number(record.stock || 0).toFixed(2).replace(/\.00$/, '')
      adjustForm.unitDisplay = unitText(record.unit)
      adjustForm.target = Number(record.stock || 0)
      adjustForm.remark = ''
      adjustVisible.value = true
    }

    const handleAdjustSubmit = async () => {
      if (adjustForm.target == null || adjustForm.target < 0) {
        message.error('库存不能为负'); return
      }
      if (Math.abs(adjustDelta.value) < 0.005) {
        message.warning('库存未变化')
        adjustVisible.value = false
        return
      }
      adjustSubmitting.value = true
      try {
        const result = await materialApi.adjustStock({
          materialId: adjustForm.materialId,
          targetStock: adjustForm.target,
          remark: adjustForm.remark
        })
        if (result.code === '0') {
          message.success('调整成功')
          adjustVisible.value = false
          loadList()
        } else {
          message.error(result.msg || '调整失败')
        }
      } finally {
        adjustSubmitting.value = false
      }
    }
    const imageFileList = ref([])

    const beforeUpload = async (file) => {
      if (!file.type.startsWith('image/')) {
        message.error('只能上传图片文件！')
        return false
      }
      if (file.size / 1024 / 1024 > 5) {
        message.error('图片大小不能超过 5MB!')
        return false
      }
      try {
        const result = await uploadApi.uploadImage(file)
        if (result?.data?.url) {
          form.imageUrl = result.data.url
          imageFileList.value = [{ uid: '-1', name: 'image', status: 'done', url: result.data.url }]
        } else {
          message.error('图片上传失败')
        }
      } catch (e) {
        console.error(e)
        message.error('图片上传失败')
      }
      return false
    }

    const handleImageRemove = () => {
      form.imageUrl = ''
      imageFileList.value = []
      return true
    }

    const getScrollContainer = () => document.querySelector('.content') || window

    const unitOptions = [
      { value: '米' }, { value: '码' }, { value: '尺' },
      { value: '个' }, { value: '对' }, { value: '包' },
      { value: '卷' }, { value: '件' }, { value: '套' },
      { value: '公斤' }, { value: '克' }
    ]
    const unitFilter = (input, option) => (option.value || '').includes(input)

    const columns = [
      { title: '图片', key: 'image', width: 70, fixed: 'left' },
      { title: '名称', dataIndex: 'name', key: 'name', width: 200, ellipsis: true, fixed: 'left' },
      { title: '类型', key: 'type', width: 70 },
      { title: '单位', key: 'unit', width: 60 },
      { title: '规格', dataIndex: 'specification', key: 'specification', width: 90, ellipsis: true },
      { title: '当前库存', key: 'stock', width: 110, align: 'right' },
      { title: '累计采购', key: 'totalPurchased', width: 90, align: 'right' },
      { title: '累计使用', key: 'totalUsed', width: 90, align: 'right' },
      { title: '最近单价', key: 'latestUnitPrice', width: 90, align: 'right' },
      { title: '供应商', key: 'suppliers', width: 200, ellipsis: true },
      { title: '备注', dataIndex: 'remark', key: 'remark', width: 150, ellipsis: true },
      { title: '操作', key: 'action', width: 200, fixed: 'right' }
    ]

    const typeLabel = (t) => ({ FABRIC: '面料', ACCESSORY: '辅料', CONSUMABLE: '耗材' })[t] || t
    const typeColor = (t) => ({ FABRIC: 'cyan', ACCESSORY: 'purple', CONSUMABLE: 'orange' })[t] || 'default'

    const unitText = (u) => {
      if (!u) return ''
      const legacy = { METER: '米', YARD: '码', PIECE: '个', PAIR: '对', PACK: '包' }
      return legacy[u] || u
    }
    const formatNum = (n) => {
      const v = Number(n || 0)
      return v.toFixed(2).replace(/\.00$/, '')
    }
    const stockClass = (r) => {
      const s = Number(r.stock || 0)
      if (s <= 0) return 'stock-empty'
      const p = Number(r.totalPurchased || 0)
      if (p > 0 && s / p < 0.2) return 'stock-low'
      return 'stock-ok'
    }

    const loadList = async () => {
      loading.value = true
      try {
        const result = await materialApi.listMaterial({
          type: searchForm.type,
          name: searchForm.name || undefined,
          supplier: searchForm.supplier || undefined
        })
        if (result.code === '0') list.value = result.data || []
      } finally {
        loading.value = false
      }
    }

    const loadSuppliers = async () => {
      try {
        const result = await materialApi.listSuppliers()
        if (result.code === '0') {
          suppliers.value = (result.data || []).map(s => ({ value: s }))
        }
      } catch (e) { console.error(e) }
    }

    const handleReset = () => {
      searchForm.type = undefined
      searchForm.name = ''
      searchForm.supplier = ''
      loadList()
    }

    const openEdit = (record) => {
      if (record) {
        form.id = record.id
        form.name = record.name
        form.type = record.type
        form.unit = unitText(record.unit)
        form.specification = record.specification || ''
        form.imageUrl = record.imageUrl || ''
        form.supplierList = record.suppliers ? record.suppliers.split(',').filter(Boolean) : []
        form.remark = record.remark || ''
        imageFileList.value = record.imageUrl ? [{ uid: '-1', name: 'image', status: 'done', url: record.imageUrl }] : []
      } else {
        Object.assign(form, { id: null, name: '', type: 'FABRIC', unit: '米', specification: '', imageUrl: '', supplierList: [], remark: '' })
        imageFileList.value = []
      }
      editVisible.value = true
    }

    const handleSubmit = async () => {
      if (!form.name) { message.error('请输入名称'); return }
      submitting.value = true
      try {
        const result = await materialApi.saveMaterial({
          id: form.id, name: form.name, type: form.type, unit: form.unit,
          specification: form.specification, imageUrl: form.imageUrl,
          suppliers: (form.supplierList || []).join(','),
          remark: form.remark
        })
        if (result.code === '0') {
          message.success('保存成功')
          editVisible.value = false
          loadList()
        } else {
          message.error(result.msg || '保存失败')
        }
      } finally {
        submitting.value = false
      }
    }

    const handleDelete = async (record) => {
      try {
        const result = await materialApi.deleteMaterial(record.id)
        if (result.code === '0') {
          message.success('删除成功')
          loadList()
        } else {
          message.error(result.msg || '删除失败')
        }
      } catch (e) { console.error(e) }
    }

    onMounted(() => { loadList(); loadSuppliers() })

    return {
      loading, submitting, list, searchForm,
      columns, editVisible, form,
      unitOptions, unitFilter,
      getScrollContainer,
      suppliers, supplierFilter, supplierOptions,
      adjustVisible, adjustSubmitting, adjustForm, adjustDelta,
      openStockAdjust, handleAdjustSubmit,
      imageFileList, beforeUpload, handleImageRemove,
      unitText, formatNum, stockClass, typeLabel, typeColor,
      loadList, handleReset, openEdit, handleSubmit, handleDelete
    }
  }
}
</script>

<style scoped>
.material-list { padding: 0; }
.page-header {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;
}
.page-header h2 { margin: 0; }
.filter-card { margin-bottom: 16px; }

.stock-ok { color: #52c41a; font-weight: 600; }
.stock-low { color: #fa8c16; font-weight: 600; }
.stock-empty { color: #999; }

.stock-cell {
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}
.stock-cell:hover {
  background-color: #e6f7ff;
}

.adjust-delta {
  margin-top: 8px;
  font-size: 13px;
}
.adjust-delta.plus { color: #52c41a; }
.adjust-delta.minus { color: #ff4d4f; }

.no-image {
  width: 48px;
  height: 48px;
  background: #f5f5f5;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
  font-size: 12px;
}
</style>

<style>
.spec-tooltip {
  max-width: 360px;
}
.spec-tooltip .ant-tooltip-inner {
  color: #1f2937 !important;
  background-color: #fff !important;
  font-size: 13px;
  line-height: 1.6;
  padding: 10px 14px !important;
  border-radius: 8px !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.08),
    0 4px 12px rgba(0, 0, 0, 0.04) !important;
  min-height: auto !important;
}
.spec-tooltip .ant-tooltip-arrow {
  width: 14px;
  height: 14px;
}
.spec-tooltip .ant-tooltip-arrow-content,
.spec-tooltip .ant-tooltip-arrow::before {
  background-color: #fff !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06) !important;
}
</style>
